import { useEffect, useRef, useState } from "react";
import type { FormEvent } from "react";
import { CHAT_MAX_LENGTH, EMOJI_REACTIONS } from "@grundbesitz/shared";
import type { RoomView } from "@grundbesitz/shared";
import { unreadChat } from "../../lib/gameView";
import { useStore } from "../../lib/store";
import { Button } from "../ui/Button";
import { useGameLog } from "./hooks";
import type { LogEntry } from "./hooks";
import { useUiStore } from "./uiStore";

type Pane = "log" | "chat";

const toneClass: Record<LogEntry["tone"], string> = {
  good: "border-ok",
  bad: "border-danger",
  info: "border-accent",
  neutral: "border-line",
};

/** Keeps a scroll container pinned to the bottom while new content arrives, unless the reader scrolled up. */
function usePinnedScroll<T extends HTMLElement>(dep: unknown) {
  const ref = useRef<T>(null);
  const pinned = useRef(true);
  useEffect(() => {
    const node = ref.current;
    if (node && pinned.current) node.scrollTop = node.scrollHeight;
  }, [dep]);
  const onScroll = () => {
    const node = ref.current;
    if (node) pinned.current = node.scrollHeight - node.scrollTop - node.clientHeight < 48;
  };
  return { ref, onScroll };
}

function LogList({ entries }: { entries: LogEntry[] }) {
  const { ref, onScroll } = usePinnedScroll<HTMLDivElement>(entries.length);
  return (
    <div ref={ref} onScroll={onScroll} className="min-h-0 flex-1 overflow-y-auto pr-1" tabIndex={0} aria-label="Spielverlauf">
      {entries.length === 0 ? (
        <p className="p-2 text-sm text-ink-2">Noch nichts passiert. Hier erscheinen alle Züge und Zahlungen.</p>
      ) : (
        <ol className="space-y-1">
          {entries.map((e) => (
            <li
              key={e.id}
              className={`border-l-4 py-1 pl-2.5 text-sm leading-snug ${toneClass[e.tone]} ${e.involvesSelf ? "font-semibold" : ""}`}
            >
              {e.text}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

function ChatView({ room }: { room: RoomView }) {
  const sendChat = useStore((s) => s.sendChat);
  const react = useStore((s) => s.react);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const last = room.chat[room.chat.length - 1]?.id ?? 0;
  const { ref, onScroll } = usePinnedScroll<HTMLDivElement>(last);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const value = text.trim();
    if (!value || sending) return;
    setSending(true);
    const ack = await sendChat(value);
    setSending(false);
    if (ack.ok) setText("");
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-2">
      <div ref={ref} onScroll={onScroll} className="min-h-0 flex-1 overflow-y-auto pr-1" tabIndex={0} aria-label="Chatnachrichten">
        {room.chat.length === 0 ? (
          <p className="p-2 text-sm text-ink-2">Noch keine Nachrichten.</p>
        ) : (
          <ul className="space-y-1.5">
            {room.chat.map((m) =>
              m.kind === "system" ? (
                <li key={m.id} className="text-center text-xs text-ink-2">
                  {m.text}
                </li>
              ) : (
                <li key={m.id} className={`flex flex-col ${m.playerId === room.you.playerId ? "items-end" : "items-start"}`}>
                  <span className="text-xs text-ink-2">{m.name}</span>
                  <span
                    className={`max-w-[85%] whitespace-pre-wrap break-words rounded-2xl px-3 py-1.5 text-sm ${
                      m.playerId === room.you.playerId ? "bg-accent text-accent-ink" : "bg-surface-2 text-ink"
                    }`}
                  >
                    {m.text}
                  </span>
                </li>
              ),
            )}
          </ul>
        )}
      </div>
      <div className="flex flex-wrap gap-1" role="group" aria-label="Reaktionen">
        {EMOJI_REACTIONS.map((emoji) => (
          <button
            key={emoji}
            type="button"
            onClick={() => void react(emoji)}
            aria-label={`Reaktion ${emoji} senden`}
            className="grid size-11 place-items-center rounded-lg border border-line bg-surface text-xl hover:bg-surface-2"
          >
            {emoji}
          </button>
        ))}
      </div>
      <form onSubmit={submit} className="flex gap-2">
        <label className="sr-only" htmlFor="gb-chat-input">
          Nachricht
        </label>
        <input
          id="gb-chat-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          maxLength={CHAT_MAX_LENGTH}
          autoComplete="off"
          placeholder="Nachricht schreiben"
          className="min-h-11 min-w-0 flex-1 rounded-lg border border-line bg-surface px-3 text-base text-ink placeholder:text-ink-2"
        />
        <Button type="submit" variant="primary" disabled={sending || text.trim() === ""}>
          Senden
        </Button>
      </form>
    </div>
  );
}

/** Tab "Chat & Log": the game log and the chat, one at a time. `visible` marks chat messages as read. */
export function ChatLogPanel({ room, visible }: { room: RoomView; visible: boolean }) {
  const [pane, setPane] = useState<Pane>("log");
  const entries = useGameLog(room);
  const chatSeenId = useUiStore((s) => s.chatSeenId);
  const markChatSeen = useUiStore((s) => s.markChatSeen);
  const last = room.chat[room.chat.length - 1]?.id ?? 0;
  const unread = unreadChat(room.chat, chatSeenId, room.you.playerId);

  useEffect(() => {
    if (visible && pane === "chat") markChatSeen(last);
  }, [visible, pane, last, markChatSeen]);

  return (
    <div className="flex h-full min-h-0 flex-col gap-3">
      <div role="tablist" aria-label="Verlauf oder Chat" className="grid grid-cols-2 gap-1 rounded-lg bg-surface-2 p-1">
        {(["log", "chat"] as const).map((p) => (
          <button
            key={p}
            role="tab"
            type="button"
            aria-selected={pane === p}
            onClick={() => setPane(p)}
            className={`min-h-10 rounded-md text-sm font-semibold ${pane === p ? "bg-surface text-ink shadow-sm" : "text-ink-2 hover:text-ink"}`}
          >
            {p === "log" ? "Verlauf" : "Chat"}
            {p === "chat" && unread > 0 && pane !== "chat" && (
              <span className="ml-1.5 rounded-full bg-accent px-1.5 py-0.5 text-xs text-accent-ink">
                {unread}
                <span className="sr-only"> ungelesen</span>
              </span>
            )}
          </button>
        ))}
      </div>
      {pane === "log" ? <LogList entries={entries} /> : <ChatView room={room} />}
    </div>
  );
}
